import { useQuery } from '@tanstack/react-query';
import { registrationQueries } from '@/features/registration';

export interface SearchableItem {
  value: string;
  label: string;
  sublabel?: string;
}

export interface RegistrationLiveItem {
  id: number;
  container_number: string;
  expected_page_count?: number | null;
  container_type?: { id: number; name: string; code: string } | null;
  entity_name_ar?: string;
}

interface UseQrLiveSearchOptions {
  manualInput: string;
  stage: string;
  enabled: boolean;
  searchableItems?: SearchableItem[];
}

interface UseQrLiveSearchResult {
  liveResults: SearchableItem[] | RegistrationLiveItem[];
  isLiveSearching: boolean;
}

export function useQrLiveSearch({
  manualInput,
  stage,
  enabled,
  searchableItems
}: UseQrLiveSearchOptions): UseQrLiveSearchResult {
  // In photo-scanning the user types the full page QR (e.g. "REG-2026-001-P3"),
  // but the backend indexes by container number — strip the page suffix before searching.
  // Also omit current_stage: containers in a photo-scanning batch may be at any stage.
  const isPhotoScanning = stage === 'photo-scanning';
  const liveSearchTerm = isPhotoScanning
    ? manualInput.replace(/-P\d+$/i, '')
    : manualInput;

  const { data: liveData, isFetching: isLiveSearching } = useQuery({
    ...registrationQueries.list({
      search: liveSearchTerm,
      ...(isPhotoScanning ? {} : { current_stage: stage }),
      per_page: 10
    }),
    enabled: enabled && !searchableItems && liveSearchTerm.length >= 3
  });

  let liveResults: SearchableItem[] | RegistrationLiveItem[];

  if (searchableItems) {
    liveResults =
      manualInput.length >= 1
        ? searchableItems.filter((item) =>
            item.value.toLowerCase().includes(manualInput.toLowerCase())
          )
        : [];
  } else if (stage === 'photo-scanning') {
    // Expand each container into individual page QR items
    const containers = (liveData?.data ?? []) as RegistrationLiveItem[];
    const expanded: SearchableItem[] = containers.flatMap((container) => {
      const count = container.expected_page_count ?? 0;
      return Array.from({ length: count }, (_, i) => ({
        value: `${container.container_number}-P${i + 1}`,
        label: `${container.container_number}-P${i + 1}`,
        sublabel: container.container_type?.name ?? container.entity_name_ar
      }));
    });
    // Filter by full manualInput so typing "REG-2026-001-P3" narrows to P3
    liveResults =
      manualInput.length >= 1
        ? expanded.filter((p) =>
            p.value.toLowerCase().includes(manualInput.toLowerCase())
          )
        : expanded;
  } else {
    liveResults = (liveData?.data ?? []) as RegistrationLiveItem[];
  }

  return { liveResults, isLiveSearching };
}
